import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../store/slices/authSlice';
import { usePermission } from '../../hooks/usePermission';
import { ROUTE_PERMISSIONS } from '../../config/routePermissions';
import {
  LayoutDashboard, Users, Megaphone, Zap, Cake, MessageSquare, CheckCircle2,
  Calendar, HandHeart, BookOpen, RefreshCw, Hand, DollarSign, Bot,
  ClipboardList, ScrollText, TrendingUp, Smartphone, Church, Settings,
  User, Key, LogOut, Cross, BarChart3, Bell, Send, Gem, LayoutGrid,
  MessageSquareQuote, Newspaper, BadgeDollarSign, Globe,
} from 'lucide-react';
import './Layout.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { canAccessRoles } = usePermission();   

  const currentPath = location.pathname.substring(1) || 'dashboard';

  const navGroups = {
    main: {
      label: 'Main',
      items: [
        { id: 'dashboard',  path: '/dashboard',  label: 'Dashboard',     icon: LayoutDashboard },
        { id: 'members',    path: '/members',    label: 'Members',       icon: Users},
        { id: 'broadcast',  path: '/broadcast',  label: 'Broadcast SMS', icon: Megaphone },
        { id: 'automation', path: '/automation', label: 'Automation',    icon: Zap },
      ],
    },
    engagement: {
      label: 'Engagement',
      items: [
        { id: 'birthdays',   path: '/birthdays',   label: 'Birthdays',        icon: Cake },
        { id: 'polls',       path: '/polls',       label: 'Polls & Feedback', icon: BarChart3 },
        // { id: 'inbox',       path: '/inbox',       label: 'SMS Inbox',        icon: MessageSquare},
        // { id: 'inbox',       path: '/inbox',       label: 'SMS Inbox',        icon: MessageSquare, badge: '5' },
        { id: 'attendance',  path: '/attendance',  label: 'Attendance',       icon: CheckCircle2 },
        { id: 'events',      path: '/events',      label: 'Events & RSVP',    icon: Calendar },
        { id: 'prayers',     path: '/prayers',     label: 'Prayer Wall',      icon: HandHeart },
        { id: 'devotionals', path: '/devotionals', label: 'Devotionals',      icon: BookOpen },
      ],
    },
    growth: {
      label: 'Growth',
      items: [
        { id: 'drips',    path: '/drips',    label: 'Drip Campaigns',    icon: RefreshCw },
        { id: 'visitors', path: '/visitors', label: 'Visitor Follow-up', icon: Hand },
        { id: 'giving',   path: '/giving',   label: 'Giving Campaigns',  icon: DollarSign },
        // { id: 'chatbot',  path: '/chatbot',  label: 'SMS Chatbot',       icon: Bot },
        // { id: 'health',   path: '/health',   label: 'Member Health',     icon: HandHeart },
      ],
    },
    dataReports: {
      label: 'Data & Reports',
      items: [
        { id: 'groups',    path: '/groups',    label: 'Groups Manager',    icon: Users },
        { id: 'templates', path: '/templates', label: 'Templates Library', icon: ClipboardList },
        { id: 'msglog',    path: '/msglog',    label: 'Message Log',       icon: ScrollText },
        // { id: 'analytics', path: '/analytics', label: 'Analytics',         icon: TrendingUp },
      ],
    },
    system: {
      label: 'System',
      items: [
        { id: 'organization', path: '/organization', label: 'Organization', icon: Smartphone },
        { id: 'branches',     path: '/branches',     label: 'Branch',       icon: Church },
        // { id: 'shortcode',    path: '/shortcode',    label: 'Shortcode',    icon: Smartphone },
        // { id: 'optout',       path: '/optout',       label: 'Opt-Out',      icon: Bell },
        // { id: 'billing',      path: '/billing',      label: 'Billing',      icon: DollarSign },
        // { id: 'pricing',      path: '/pricing',      label: 'Pricing',      icon: Gem },
        // { id: 'sender-ids',   path: '/sender-ids',   label: 'Sender ID',    icon: Bell },
        { id: 'sms-balance',  path: '/sms-balance',  label: 'SMS Balance',  icon: Send },
        { id: 'settings',     path: '/settings',     label: 'Settings',     icon: Settings },
      ],
    },
    userManagement: {
      label: 'User Management',
      items: [
        { id: 'users',       path: '/users',       label: 'User Management',     icon: User },
        { id: 'roles',       path: '/roles',       label: 'Roles',               icon: Key },
        { id: 'permissions', path: '/permissions', label: 'Permissions',         icon: Key },
        { id: 'profile',     path: '/profile',     label: 'User Profile',        icon: User },
      ],
    },
    frontend: {
      label: 'Frontend',
      items: [
        { id: 'features',     path: '/features',     label: 'Features',     icon: LayoutGrid },
        { id: 'testimonials', path: '/testimonials', label: 'Testimonials', icon: MessageSquareQuote },
        { id: 'blogs',        path: '/blogs',        label: 'Blogs',        icon: Newspaper },
        { id: 'plan-pricing', path: '/plan-pricing', label: 'Pricing',      icon: BadgeDollarSign },
        { id: 'regions',      path: '/regions',      label: 'Regions',      icon: Globe },
        { id: 'stats',        path: '/stats',        label: 'Stats',        icon: BarChart3 },
      ],
    },
  };

  /* Visibility derived from the SHARED config */
  const visibleGroups = Object.values(navGroups)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        canAccessRoles(ROUTE_PERMISSIONS[item.path])
      ),
    }))
    .filter((group) => group.items.length > 0);

  const handleNavigate = (path) => navigate(path);
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };
  const isActive = (path) => location.pathname === path;

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="logo-mark">
          <div className="logo-icon"><Cross size={20} /></div>
          <div>
            <div className="logo-text">FaithConnect</div>
            <div className="logo-sub">SMS Platform</div>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {visibleGroups.map((group) => (
          <div key={group.label} className="nav-section">
            <div className="nav-label">{group.label}</div>
            {group.items.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavigate(item.path)}
              >
                <span className="nav-icon"><item.icon size={18} /></span>
                <span>{item.label}</span>
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-bottom">
        <div className="sms-credit-box">
          <div className="credit-label">SMS Credits Remaining</div>
          <div className="credit-bar-wrap">
            <div className="credit-bar" style={{ width: '65%' }} />
          </div>
          <div className="credit-count">5,234 / 8,000</div>
        </div>
        <button
          className="nav-item"
          onClick={handleLogout}
          style={{ color: '#ef4444', marginTop: '8px' }}
        >
          <span className="nav-icon"><LogOut size={18} /></span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;